<script setup lang="ts">
import { computed } from 'vue';
import { useEditorStore } from '@/stores/editor';
import { useMapStore } from '@/stores/map';
import { useTools } from '@/composables/useTools';
import { TileFlagKey } from '@/formats/types';

const editorStore = useEditorStore();
const mapStore = useMapStore();
const tools = useTools();

const FLAGS: Array<{ key: TileFlagKey; label: string; hint?: string }> = [
    { key: 'blocked', label: 'Blocked' },
    { key: 'teleport', label: 'Teleport' },
    { key: 'farming', label: 'Farming' },
    { key: 'buildable', label: 'Buildable', hint: 'Saved to the file, but no client or server reads it' },
];

// Tile under the cursor. Read-only: the cursor leaves the tile on its way to
// this panel, so edits are made to the selection instead.
const hoveredTile = computed(() => {
    return mapStore.getTile(editorStore.tileX, editorStore.tileY);
});

// How many selected tiles have each flag / an object
const selectionStats = computed(() => {
    const bounds = editorStore.selectionBounds;
    const doc = mapStore.document;
    if (!bounds || !doc) return null;

    const counts: Record<TileFlagKey, number> = { blocked: 0, teleport: 0, farming: 0, buildable: 0 };
    let objects = 0;

    for (let x = bounds.left; x <= bounds.right; x++) {
        for (let y = bounds.top; y <= bounds.bottom; y++) {
            const tile = doc.tiles[x]?.[y];
            if (!tile) continue;
            if (tile.blocked) counts.blocked++;
            if (tile.teleport) counts.teleport++;
            if (tile.farming) counts.farming++;
            if (tile.buildable) counts.buildable++;
            if (tile.objectSprite > 0) objects++;
        }
    }

    return { counts, objects, total: bounds.width * bounds.height };
});

// Set the flag on every selected tile, or clear it if they all have it
function toggleSelectionFlag(flag: TileFlagKey): void {
    const stats = selectionStats.value;
    if (!stats) return;

    tools.editSelection({ [flag]: stats.counts[flag] < stats.total });
}
</script>

<template>
    <div class="properties-panel">
        <div class="panel-header">
            <h3>Properties</h3>
        </div>

        <div class="panel-content">
            <!-- Selection: the editable part -->
            <div class="property-section" v-if="selectionStats && editorStore.selectionBounds">
                <h4>Selection</h4>
                <div class="property-row">
                    <span class="property-label">From:</span>
                    <span class="property-value">{{ editorStore.selectionBounds.left }}, {{ editorStore.selectionBounds.top }}</span>
                </div>
                <div class="property-row">
                    <span class="property-label">Size:</span>
                    <span class="property-value">{{ editorStore.selectionBounds.width }} x {{ editorStore.selectionBounds.height }}</span>
                </div>

                <label class="flag-toggle" v-for="flag in FLAGS" :key="flag.key" :title="flag.hint">
                    <input
                        type="checkbox"
                        :checked="selectionStats.counts[flag.key] === selectionStats.total"
                        :indeterminate="selectionStats.counts[flag.key] > 0 && selectionStats.counts[flag.key] < selectionStats.total"
                        @change="toggleSelectionFlag(flag.key)"
                    />
                    <span>{{ flag.label }}</span>
                </label>

                <button
                    class="clear-btn"
                    :disabled="selectionStats.objects === 0"
                    @click="tools.clearSelectionObjects()"
                >
                    Clear Objects ({{ selectionStats.objects }})
                </button>
            </div>

            <!-- Hovered tile: read-only -->
            <template v-if="hoveredTile">
                <div class="property-section">
                    <h4>Tile {{ editorStore.tileX }}, {{ editorStore.tileY }}</h4>
                    <div class="property-row">
                        <span class="property-label">Base:</span>
                        <span class="property-value">{{ hoveredTile.tileSprite }} / {{ hoveredTile.tileSpriteFrame }}</span>
                    </div>
                    <div class="property-row">
                        <span class="property-label">Object:</span>
                        <span class="property-value" v-if="hoveredTile.objectSprite > 0">
                            {{ hoveredTile.objectSprite }} / {{ hoveredTile.objectSpriteFrame }}
                        </span>
                        <span class="property-value" v-else>-</span>
                    </div>
                    <div class="property-row">
                        <span class="property-label">Flags:</span>
                        <span class="property-value">
                            {{ FLAGS.filter(f => hoveredTile![f.key]).map(f => f.label).join(', ') || '-' }}
                        </span>
                    </div>
                </div>
            </template>

            <p class="panel-hint" v-if="!selectionStats">
                Select tiles with the Select tool (V) to edit their flags
            </p>
        </div>
    </div>
</template>

<style scoped>
.properties-panel {
    overflow: hidden;
    min-width: 180px;
}

.panel-header {
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px 8px 0 0;
}

.panel-header h3 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
}

.panel-content {
    padding: 12px;
}

.panel-hint {
    color: var(--text-secondary);
    font-size: 11px;
    line-height: 1.4;
    max-width: 180px;
}

.property-section {
    margin-bottom: 16px;
}

.property-section h4 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
    margin-bottom: 8px;
}

.property-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
    padding: 2px 0;
}

.property-label {
    color: var(--text-secondary);
}

.property-value {
    color: var(--text-primary);
    font-family: monospace;
}

.flag-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 12px;
    cursor: pointer;
}

.flag-toggle input {
    width: 14px;
    height: 14px;
    cursor: pointer;
}

.flag-toggle span {
    color: var(--text-primary);
}

.clear-btn {
    width: 100%;
    margin-top: 8px;
    padding: 6px 12px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.clear-btn:hover:not(:disabled) {
    background: var(--error-color);
    border-color: var(--error-color);
    color: white;
}

.clear-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
</style>
